import deepmerge from "deepmerge";
import { selector, useRecoilValue, useSetRecoilState } from "recoil";
import { getCustomerSettings } from "../CustomerSettings";
import { defaultTheme, defaultThemeOptions } from "../themes/defaultTheme";
import { yellowTheme, yellowThemeOptions } from "../themes/yellowTheme";
import { createTheme } from "@material-ui/core";
import { deepCopy, getProperty, setProperty } from "../utils/FPEUtils";
import { useCallback } from "react";
import { userConfigState } from "./UserConfigState";

const THEME_COLOR_DEFAULT = "type1";
const THEME_COLOR_YELLOW = "type2";

const convert = (themeList) => {
  const saveData = themeList.map(theme => {
    const t = {
      name: theme.name,
      themeOptions: theme.themeOptions,
      iconColor: theme.iconColor,
      default: theme.default
    };
    if (theme.default) {
      t.defaultThemeOptions = theme.defaultThemeOptions;
    }
    return t;
  });
  return saveData;
};

/**
 * テーマの一覧
 */
const themeListSelector = selector({
  key: 'themeListSelector',
  get: ({get}) => {
    const theme = get(userConfigState('theme'));
    if (theme && theme.theme_list) {
      const savedValue = theme.theme_list.map(t => {
        const theme = deepmerge({}, t);
        if (theme.default) {
          theme.defaultThemeOptions = theme.name === 'type1' ? defaultThemeOptions : yellowThemeOptions;
        }
        theme.themeOptions = deepmerge(theme.defaultThemeOptions, theme.themeOptions);
        theme.theme = createTheme(theme.themeOptions);
        return theme;
      });
      return savedValue;
    }
    return [
      {
        name: THEME_COLOR_DEFAULT,
        theme: defaultTheme,
        themeOptions: defaultThemeOptions,
        defaultThemeOptions: defaultThemeOptions,
        iconColor: "#7cc3e1",
        default: true
      },
      {
        name: THEME_COLOR_YELLOW,
        theme: yellowTheme,
        themeOptions: yellowThemeOptions,
        defaultThemeOptions: yellowThemeOptions,
        iconColor: "#faefc2",
        default: true
      }
    ];
  },
  set: ({get, set}, themeList) => {
    const theme = get(userConfigState('theme'));
    const newTheme = {...theme, theme_list: convert(themeList)};
    set(userConfigState('theme'), newTheme);
  },
  dangerouslyAllowMutability: true
});

/**
 * 選択中のテーマ名
 */
const activeThemeNameSelector = selector({
  key: 'selectedThemeNameSelector',
  get: ({get}) => {
    const theme = get(userConfigState('theme'));
    if (theme && theme.selected_theme_name) {
      return theme.selected_theme_name;
    }
    return getCustomerSettings('themeColor') || THEME_COLOR_DEFAULT;
  },
  set: ({get, set}, selectedThemeName) => {
    const theme = get(userConfigState('theme'));
    const newTheme = {...theme, selected_theme_name: selectedThemeName};
    set(userConfigState('theme'), newTheme);
  },
  dangerouslyAllowMutability: true
});

/**
 * 選択中のテーマ
 */
const activeThemeSelector = selector({
  key: 'activeThemeSelector',
  get: ({get}) => {
    const activeThemeName = get(activeThemeNameSelector);
    const list = get(themeListSelector);
    return list.find(theme => theme.name === activeThemeName);
  },
  dangerouslyAllowMutability: true
});

/**
 * テーマ追加（コピー）.
 */
const addThemeSelector = selector({
  key: 'addThemeSelector',
  set: ({get, set}, param) => {
    const oldList = get(themeListSelector);

    const dupIdx = oldList.findIndex(target => {
      return target.name === param.themeName;
    });
    if (dupIdx !== -1) return;

    const idx = oldList.findIndex(target => {
      return target.name === param.sourceName;
    });
    if (idx === -1) return;
    const themeObj = createTheme(oldList[idx].themeOptions);
    const newTheme = {
      name: param.themeName,
      theme: themeObj,
      themeOptions: deepCopy(oldList[idx].themeOptions),
      iconColor: param.iconColor ? param.iconColor : '#333',
      default: false
    };
    const newList = [...oldList, newTheme];
    set(userConfigState('theme'), {
      theme_list: convert(newList),
      selected_theme_name: param.themeName
    });
  },
  get: () => {},
  dangerouslyAllowMutability: true
});

/**
 * テーマ更新.
 */
const changeThemeColorSelector = selector({
  key: "changeThemeColorSelector",
  set: ({get, set}, param) => {
    const list = get(themeListSelector);
    const idx = list.findIndex(target => {
      return target.name === param.name;
    });
    if (idx === -1) return;
    const target = {...list[idx]};
    const themeOptions = deepCopy(target.themeOptions);
    setProperty(themeOptions, param.key, param.color);
    target.themeOptions = themeOptions;
    target.theme = createTheme(themeOptions);
    const newList = [...list];
    newList.splice(idx, 1, target);
    set(themeListSelector, newList);
  },
  get: () => {},
  dangerouslyAllowMutability: true
})

/**
 * デフォルト状態に戻す(デフォルトテーマのみ可能）.
 */
const restoreDefaultThemeSelector = selector({
  key: 'restoreDefaultThemeSelector',
  set: ({get, set}, name) => {
    const list = get(themeListSelector);
    const idx = list.findIndex(target => {
      return target.name === name;
    });
    if (idx === -1) return;
    const target = {...list[idx]};
    if (!target.default) return;
    target.themeOptions = target.defaultThemeOptions;
    target.theme = createTheme(target.defaultThemeOptions);
    const newList = [...list];
    newList.splice(idx, 1, target);
    set(themeListSelector, newList);
  },
  get: () => {},
  dangerouslyAllowMutability: true
});

/**
 * テーマ削除.
 */
const removeThemeSelector = selector({
  key: 'removeThemeSelector',
  set: ({get, set}, name) => {
    const oldList = get(themeListSelector);
    const idx = oldList.findIndex(target => {
      return target.name === name;
    });
    if (idx === -1) return;

    const newList = [...oldList.slice(0, idx), ...oldList.slice(idx + 1)];
    set(userConfigState('theme'), {
      theme_list: convert(newList),
      selected_theme_name: THEME_COLOR_DEFAULT
    });
  },
  get: () => {},
  dangerouslyAllowMutability: true
});

/**
 * テーマのアイコンカラー変更
 */
const changeThemeIconColorSelector = selector({
  key: 'changeThemeIconColorSelector',
  set: ({get, set}, param) => {
    const list = get(themeListSelector);
    const idx = list.findIndex(target => {
      return target.name === param.name;
    });
    if (idx === -1) return;
    const target = {...list[idx]};
    target.iconColor = param.color;
    const newList = [...list];
    newList.splice(idx, 1, target);
    set(themeListSelector, newList);
  },
  get: () => {},
  dangerouslyAllowMutability: true
});

export const useThemeColorState = () => {
  const theme = useRecoilValue(activeThemeSelector);
  const themes = useRecoilValue(themeListSelector);
  const setActiveThemeName = useSetRecoilState(activeThemeNameSelector);
  const addTheme = useSetRecoilState(addThemeSelector);
  const changeThemeColor = useSetRecoilState(changeThemeColorSelector);
  const restoreDefault = useSetRecoilState(restoreDefaultThemeSelector);
  const removeTheme = useSetRecoilState(removeThemeSelector);
  const changeThemeIconColor = useSetRecoilState(changeThemeIconColorSelector)

  const getColor = useCallback(key => {
    return getProperty(theme.themeOptions, key)
  }, [theme]);

  return {
    themeName: theme.name,
    theme: theme.theme,
    themeList: themes,
    themeInfo : theme,
    getColor,
    changeTheme : setActiveThemeName,
    addTheme,
    changeThemeColor: changeThemeColor,
    restoreDefault,
    removeTheme,
    changeThemeIconColor
  }
}